import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Banknote, Coins, type LucideIcon, MapPin } from 'lucide-react';

import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { PropertyTypesProvider } from '@modules/_shared/providers/PropertyTypesProvider';
import { IconBadge } from '@modules/_shared/shared/components/IconBadge';
import { formatDistance } from '@modules/_shared/utils/formatting';
import { useTransactionPresentation } from '@modules/reports/cma-reports/step-transaction/transactionPresentation';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

import { InfoTip } from './ChartControls';
import {
  type ListingTransactions,
  type TransactionRow,
  useListingTransactions,
} from './useListingTransactions';

const CARD_WIDTH = 280;

const MedianStat = ({
  icon,
  label,
  help,
  value,
}: {
  icon: LucideIcon;
  label: string;
  help: string;
  value: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
    <IconBadge icon={icon} color="primary.main" />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '16px', fontWeight: 800, lineHeight: 1.15 }}>
        {value}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, color: 'text.secondary', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <InfoTip title={help} />
      </Box>
    </Box>
  </Box>
);

const TransactionItemCard = ({ row }: { row: TransactionRow }) => {
  const { t, locale, countryCode } = useLocale();
  const currency = getCurrencyByCountryCode(countryCode);
  const theme = useTheme();
  const presentation = useTransactionPresentation(row);

  const { priceEvolution } = row;
  const evolutionPct =
    priceEvolution != null
      ? Number((priceEvolution.rate * 100).toFixed(1))
      : null;
  const evolutionChangeLabel =
    evolutionPct != null
      ? `${evolutionPct >= 0 ? '+' : ''}${evolutionPct.toFixed(1)}%`
      : null;

  return (
    <Box
      sx={{
        width: CARD_WIDTH,
        minWidth: CARD_WIDTH,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box>
        <Typography sx={{ lineHeight: 1.3, fontWeight: 700, fontSize: '14px' }}>
          {presentation.propertyTypeLabel}
          {presentation.roomsLabel && ` • ${presentation.roomsLabel}`}
          {presentation.surfaceLabel && ` • ${presentation.surfaceLabel}`}
        </Typography>
        <Typography
          component="div"
          sx={{
            ...theme.typography.body2,
            color: (theme.vars ?? theme).palette.grey['800'],
            fontSize: '0.8rem',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>
              {presentation.locality}
              {presentation.postcode && ` (${presentation.postcode})`}
            </span>
            <span>•</span>
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              <MapPin size={12} />
              <span>{formatDistance(presentation.distance ?? null)}</span>
            </Box>
          </Box>
        </Typography>
        {presentation.formattedDate && (
          <Typography
            sx={{
              ...theme.typography.body2,
              color: (theme.vars ?? theme).palette.grey['800'],
              fontSize: '0.8rem',
            }}
          >
            {t('Sold on')} {presentation.formattedDate}
          </Typography>
        )}
      </Box>
      {presentation.priceLabel && (
        <Box sx={{ mt: 'auto' }}>
          <Typography
            sx={{ fontSize: '16px', fontWeight: 700, lineHeight: 1.3 }}
          >
            {presentation.priceLabel}
          </Typography>
          {presentation.pricePerSqmLabel && (
            <Typography
              sx={{
                ...theme.typography.body2,
                color: (theme.vars ?? theme).palette.grey['800'],
              }}
            >
              {presentation.pricePerSqmLabel}
            </Typography>
          )}
          {priceEvolution != null && evolutionChangeLabel != null && (
            <Typography
              sx={{
                ...theme.typography.body2,
                color: (theme.vars ?? theme).palette.grey['800'],
                fontSize: '0.8rem',
              }}
            >
              {'≈ '}
              {t('{{price}} today', {
                price: formatPrice(
                  priceEvolution.adjustedPrice,
                  locale,
                  currency,
                ),
              })}
              {` (${evolutionChangeLabel})`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export const ListingTransactionsCard = ({ lotId }: { lotId: string }) => {
  const { t, locale, countryCode } = useLocale();
  const currency = getCurrencyByCountryCode(countryCode);
  const transactions: ListingTransactions = useListingTransactions(lotId);

  if (transactions.state === 'unavailable') {
    return null;
  }

  return (
    <PropertyTypesProvider>
      <ListingAccordion
        title={t('Comparable transactions')}
        loading={transactions.state === 'loading'}
      >
        {transactions.state === 'error' && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {t('Failed to load data')}
          </Typography>
        )}
        {transactions.state === 'ready' &&
          (transactions.rows.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('No transactions found nearby.')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {transactions.priceStatsUnavailable && (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                  {t('Failed to load price evolution data')}
                </Typography>
              )}
              <Stack direction="row" spacing={4}>
                <MedianStat
                  icon={Coins}
                  label={t('Median price')}
                  help={t('Median sold price across these transactions.')}
                  value={
                    transactions.medianPrice != null
                      ? formatPrice(
                          Math.round(transactions.medianPrice),
                          locale,
                          currency,
                        )
                      : '—'
                  }
                />
                <MedianStat
                  icon={Banknote}
                  label={t('Price per m²')}
                  help={t(
                    'Median sold price per m² across these transactions.',
                  )}
                  value={
                    transactions.medianPricePerSqm != null
                      ? `${formatPrice(
                          Math.round(transactions.medianPricePerSqm),
                          locale,
                          currency,
                        )} / m²`
                      : '—'
                  }
                />
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  overflowX: 'auto',
                  pb: 1,
                  scrollbarWidth: 'thin',
                }}
              >
                {transactions.rows.map(row => (
                  <TransactionItemCard key={row.id} row={row} />
                ))}
              </Box>
            </Stack>
          ))}
      </ListingAccordion>
    </PropertyTypesProvider>
  );
};
